// #region Imports
import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { defer, firstValueFrom, Observable } from 'rxjs';

import {
  INDEXED_DB_CACHE_STORAGE_KEY,
  INDEXED_DB_CACHE_VALIDITY_MINUTES,
  SITECONFIGURATION_CACHE_NAME,
  SITECONFIGURATION_KEYS_SITE,
  SITECONFIGURATION_KEYS_USER,
  SITECONFIGURATION_MENU_RETURNS_ENABLE,
  SITECONFIGURATION_VALUE_ENABLE,
} from '../app.constants';
import { AppService } from '../app.service';
import { BootstrapService } from './bootstrap.service';
// #endregion Imports

// #region TypesAndInterfaces
/** Contenuto della cache in localStorage: i valori e il tipo utente a cui appartengono. */
interface SiteConfigurationCacheEntry {
  userType: string;
  values: Record<string, string>;
}

/** Risposta di Site/GetSiteConfigurations: dizionario chiave-valore già risolto. */
interface SiteConfigurationsResponse {
  Code: number;
  Data: Record<string, string>;
}

const HTTP_STATUS_OK: number = 200;
const MILLISECONDS_PER_MINUTE: number = 1000 * 60;
// #endregion TypesAndInterfaces

// #region ComponentDecorator
/**
 * Punto unico di lettura delle configurazioni di sito usate dal frontend.
 *
 * Le chiavi arrivano dal backend in un solo giro, come dizionario chiave-valore già risolto con la
 * precedenza configurazione del sito, configurazione del sito master, configurazione globale.
 * Una chiave assente significa "non configurata": chi legge applica il proprio valore di default.
 *
 * La cache segue la stessa politica con cui si cachano i content, perché prima della migrazione
 * questi valori erano content: scadenza a tempo, nessun aggiornamento in background. Il payload
 * sta in localStorage come JSON invece che in IndexedDB, perché è una manciata di coppie
 * stringa/stringa.
 */
@Injectable({
  providedIn: 'root',
})
// #endregion ComponentDecorator
export class SiteConfigurationService {
  // #region Signals
  private readonly configurations: WritableSignal<Readonly<Record<string, string>>> = signal<
    Readonly<Record<string, string>>
  >({});
  // #endregion Signals

  // #region Computed
  /** Dizionario delle configurazioni caricate, in sola lettura */
  public readonly values: Signal<Readonly<Record<string, string>>> = computed((): Readonly<Record<string, string>> =>
    this.configurations()
  );

  /**
   * Permesso di gestione resi dell'utente, normalizzato.
   *
   * Il permesso viene scritto in localStorage come stringa, quindi vale sia 'false' sia '0' come
   * negazione; in assenza del valore si considera negato, come fanno le pagine dei resi. Il valore manca
   * quando la lettura del permesso fallisce o non risponde 200; con risposta 200 e permesso non indicato dal
   * server viene invece scritto come concesso.
   * Si legge dallo specchio di localStorage tenuto da BootstrapService: la voce Resi dell'header lo
   * legge nel template e deve aggiornarsi quando il permesso arriva o cambia dopo il primo disegno.
   */
  public readonly hasRmaPermission: Signal<boolean> = computed((): boolean => {
    const raw: string | null = this.bootstrapService.getRmaPermission();

    return !(!raw || raw.trim().toLowerCase() === 'false' || raw === '0');
  });

  /**
   * True se la voce dei resi va mostrata all'utente corrente.
   *
   * Sono tre condizioni distinte e tutte necessarie: il sito deve avere i resi attivi,
   * l'utente deve avere il permesso di gestirli, e i resi non sono previsti sui siti che
   * ereditano dal master personalizzalo.
   */
  public readonly canManageReturns: Signal<boolean> = computed((): boolean =>
    this.isEnabled(SITECONFIGURATION_MENU_RETURNS_ENABLE) &&
    this.hasRmaPermission() &&
    !this.bootstrapService.isSiteMasterPersonalizzalo()
  );
  // #endregion Computed

  // #region State
  /** Chiave di localStorage che contiene il timestamp dell'ultimo caricamento */
  private readonly cacheTimestampKey: string = INDEXED_DB_CACHE_STORAGE_KEY(
    SITECONFIGURATION_CACHE_NAME
  );

  /** Minuti di validità della cache, letti dalla stessa configurazione usata per i content */
  private readonly cacheValidityMinutes: number = INDEXED_DB_CACHE_VALIDITY_MINUTES(
    SITECONFIGURATION_CACHE_NAME
  );

  /**
   * Tetto di attesa del caricamento in fase di bootstrap.
   * Le configurazioni si caricano prima che il router renda la prima pagina, quindi un backend
   * lento non deve lasciare l'utente davanti a una pagina bianca: oltre questo tempo si prosegue
   * con i valori di default e la pagina si disegna comunque.
   */
  private readonly bootstrapTimeoutMs: number = 5000;

  /**
   * Caricamento in corso. Serve solo a unire due chiamate concorrenti in una sola richiesta, e
   * viene azzerato appena finisce: non deve diventare un lucchetto permanente, perché al login e
   * al logout cambia il tipo utente e la configurazione va richiesta di nuovo.
   */
  private loadPromise: Promise<void> | null = null;
  // #endregion State

  // #region DependencyInjection
  private readonly appService: AppService = inject(AppService);
  private readonly bootstrapService: BootstrapService = inject(BootstrapService);
  // #endregion DependencyInjection

  // #region PublicMethods

  /**
   * Carica le configurazioni del sito corrente.
   * Se la cache è ancora valida le legge da localStorage senza toccare la rete; altrimenti svuota
   * la cache, rifà la chiamata e la riscrive. Non solleva mai: in caso di errore il dizionario
   * resta vuoto e ogni lettura cade sul proprio default.
   *
   * Il caricamento parte alla sottoscrizione ed emette una sola volta, a caricamento finito o allo
   * scadere del tetto di attesa. Sotto resta la Promise condivisa: unisce le chiamate concorrenti
   * e, oltre il tetto, lascia arrivare le risposte in background; un Observable annullato dalla
   * race interromperebbe invece le richieste e il sito resterebbe sui default.
   */
  public load(): Observable<void> {
    return defer((): Promise<void> => this.startOrJoinLoad());
  }

  /**
   * Ritorna true se la configurazione indicata è attiva.
   * Il valore attivo è la stringa 'enable': qualunque altro valore, compresa l'assenza della
   * chiave, vale come non attivo.
   * @param key Chiave della configurazione
   */
  public isEnabled(key: string): boolean {
    return this.getString(key, '').trim() === SITECONFIGURATION_VALUE_ENABLE;
  }

  /**
   * Ritorna la configurazione indicata come numero.
   * @param key Chiave della configurazione
   * @param fallback Valore da usare se la chiave è assente o non numerica
   */
  public getNumber(key: string, fallback: number): number {
    const parsed: number = Number(this.getString(key, ''));

    return isNaN(parsed) || this.getString(key, '').trim() === '' ? fallback : parsed;
  }

  /**
   * Ritorna la configurazione indicata come stringa.
   * @param key Chiave della configurazione
   * @param fallback Valore da usare se la chiave è assente
   */
  public getString(key: string, fallback: string): string {
    const value: string | undefined = this.configurations()[key];

    return value === undefined || value === null ? fallback : value;
  }

  // #endregion PublicMethods

  // #region PrivateMethods

  /**
   * Ritorna il caricamento in corso, oppure ne avvia uno nuovo con il tetto di attesa del bootstrap.
   */
  private startOrJoinLoad(): Promise<void> {
    // Una sola richiesta anche se il caricamento viene invocato da più punti del bootstrap
    if (this.loadPromise != null) {
      return this.loadPromise;
    }

    this.loadPromise = Promise.race([
      this.loadFromCacheOrBackend(),
      new Promise<void>((resolve: () => void): void => {
        setTimeout(resolve, this.bootstrapTimeoutMs);
      }),
    ]).finally((): void => {
      // Finito il caricamento si libera il posto: bootstrapPage viene richiamata a ogni login e
      // logout, e in quel momento la configurazione del tipo utente precedente non vale più.
      this.loadPromise = null;
    });

    return this.loadPromise;
  }

  /**
   * Controlla se il payload in cache è ancora entro i minuti di validità.
   * Stesso criterio applicato alle tabelle di IndexedDB dei content.
   */
  private isCacheValid(): boolean {
    try {
      const latestSyncValue: string | null = localStorage.getItem(this.cacheTimestampKey);

      if (!latestSyncValue) {
        return false;
      }

      const elapsedMinutes: number = Math.abs(
        Math.round((new Date().getTime() - new Date(latestSyncValue).getTime()) / MILLISECONDS_PER_MINUTE)
      );

      return elapsedMinutes < this.cacheValidityMinutes;
    } catch (error) {
      console.error('isCacheValid', error);
      return false;
    }
  }

  /**
   * Legge il dizionario dalla cache, null se assente, illeggibile o appartenente a un altro tipo
   * utente.
   *
   * Il tipo utente è salvato insieme ai valori perché la configurazione dipende da esso: senza
   * questo controllo, dopo un cambio utente che non svuoti il localStorage si continuerebbe a
   * servire la configurazione del tipo precedente.
   */
  private readFromCache(): Record<string, string> | null {
    try {
      const raw: string | null = this.appService.getLocal(this.appService.localSiteConfiguration);

      if (!raw) {
        return null;
      }

      const parsed: SiteConfigurationCacheEntry | null = JSON.parse(raw) as SiteConfigurationCacheEntry | null;

      if (!parsed || typeof parsed !== 'object' || typeof parsed.values !== 'object') {
        return null;
      }

      if (parsed.userType !== this.bootstrapService.getUserType()) {
        return null;
      }

      return parsed.values;
    } catch (error) {
      console.error('readFromCache', error);
      return null;
    }
  }

  /**
   * Scrive il dizionario in cache, con il tipo utente a cui appartiene, e aggiorna il timestamp
   * di validità.
   */
  private writeToCache(configurations: Record<string, string>): void {
    try {
      this.appService.setLocal(
        this.appService.localSiteConfiguration,
        JSON.stringify({
          userType: this.bootstrapService.getUserType(),
          values: configurations,
        })
      );
      localStorage.setItem(this.cacheTimestampKey, new Date().toString());
    } catch (error) {
      console.error('writeToCache', error);
    }
  }

  /**
   * Svuota il payload in cache, lasciando al caricamento il compito di riscriverlo.
   */
  private clearCache(): void {
    try {
      this.appService.clearLocal(this.appService.localSiteConfiguration);
    } catch (error) {
      console.error('clearCache', error);
    }
  }

  /**
   * Legge le configurazioni dalla cache se è ancora valida, altrimenti le richiede al backend.
   * Non solleva mai: in caso di errore il dizionario resta vuoto e ogni lettura cade sul default.
   */
  private async loadFromCacheOrBackend(): Promise<void> {
    try {
      if (this.isCacheValid()) {
        const cached: Record<string, string> | null = this.readFromCache();

        if (cached !== null) {
          this.configurations.set(cached);
          return;
        }
      }

      this.clearCache();

      // Due chiamate, non una: le chiavi di sito hanno UserType NULL e vanno chieste con tipo
      // utente vuoto, quelle differenziate per tipo utente col tipo corrente. Vedi il commento
      // sui due elenchi in app.constants.
      const [siteResponse, userResponse] = await Promise.all([
        this.requestConfigurations(SITECONFIGURATION_KEYS_SITE, ''),
        this.requestConfigurations(
          SITECONFIGURATION_KEYS_USER,
          this.bootstrapService.getUserType()
        ),
      ]);

      const loaded: Record<string, string> = { ...siteResponse, ...userResponse };

      this.configurations.set(loaded);

      // Se una delle due richieste è fallita il dizionario è incompleto: salvarlo in cache
      // terrebbe il sito sui valori di default per tutta la validità della cache anche dopo
      // che il backend è tornato a rispondere.
      if (siteResponse !== null && userResponse !== null) {
        this.writeToCache(loaded);
      }
    } catch (error) {
      console.error('loadFromCacheOrBackend', error);
    }
  }

  /**
   * Richiede al backend le configurazioni delle chiavi indicate per un dato tipo utente.
   * Ritorna un dizionario vuoto se non c'è nulla da chiedere e null se la richiesta fallisce o
   * la risposta non è utilizzabile, così chi chiama non scambia un errore per «nessuna chiave».
   * @param keys Chiavi da richiedere
   * @param userType Tipo utente, stringa vuota per le chiavi valide per tutto il sito
   */
  private async requestConfigurations(
    keys: string[],
    userType: string
  ): Promise<Record<string, string> | null> {
    if (!keys || keys.length === 0) {
      return {};
    }

    let response: SiteConfigurationsResponse | undefined;
    try {
      response = await firstValueFrom(this.appService.httpPost('Site', 'GetSiteConfigurations', {
        whatList: keys,
        userType: userType,
      }));
    } catch (error) {
      console.error('requestConfigurations', userType, error);
      return null;
    }

    return response && response.Code === HTTP_STATUS_OK && response.Data
      ? response.Data
      : null;
  }

  // #endregion PrivateMethods
}
